import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Phone, Mail, ArrowRight, Users, Wifi, Coffee, Monitor, MapPin } from "lucide-react";
import { Reveal, SectionTitle } from "../components/common";
import { publicApi, mediaUrl } from "../lib/api";
import { company } from "../mock";
import Seo, { SITE_NAME } from "../components/Seo";

const lines = (s) => (s || "").split("\n").map((l) => l.trim()).filter(Boolean);
const paras = (s) => (s || "").split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
const priceValue = (s) => {
  const m = (s || "").match(/[\d.]+/);
  return m ? m[0] : "";
};

const CheckList = ({ items, testid }) => (
  <ul className="space-y-3" data-testid={testid}>
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3">
        <span className="w-6 h-6 rounded-full bg-orange/10 text-orange flex items-center justify-center shrink-0 mt-0.5"><Check size={14} strokeWidth={3} /></span>
        <span className="text-slate-600 leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

export default function MeetingRoom() {
  const [d, setD] = useState(null);
  useEffect(() => { publicApi.meetingRoom().then(setD).catch(() => {}); }, []);

  if (!d) return <div className="min-h-[50vh] flex items-center justify-center text-slate-400">Loading...</div>;

  const introParas = paras(d.intro);
  const tagline = introParas[0];
  const restIntro = introParas.slice(1);

  const gallery = [d.image_1, d.image_2, d.image_3].filter(Boolean);

  const prices = [
    { amount: d.price_hourly, note: "" },
    { amount: d.price_half_day, note: d.price_half_day_note },
    { amount: d.price_full_day, note: d.price_full_day_note },
  ].filter((p) => p.amount);

  const offers = [
    { label: "Per hour", raw: d.price_hourly },
    { label: "Half day", raw: d.price_half_day },
    { label: "Full day", raw: d.price_full_day },
  ]
    .filter((o) => priceValue(o.raw))
    .map((o) => ({
      "@type": "Offer",
      name: `Meeting room hire — ${o.label}`,
      price: priceValue(o.raw),
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
    }));

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Meeting Room Hire in Tamworth",
    serviceType: "Meeting room hire",
    description:
      "Affordable, modern meeting room hire in Tamworth for business meetings, interviews, training sessions, client presentations and content recording.",
    provider: {
      "@type": "AccountingService",
      name: SITE_NAME,
      telephone: "+44 1827 780550",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Tamworth",
        addressRegion: "Staffordshire",
        addressCountry: "GB",
      },
    },
    areaServed: { "@type": "City", name: "Tamworth" },
    ...(offers.length ? { offers } : {}),
  };

  const seoImage = gallery.find(Boolean);
  const seoDescription =
    "Affordable meeting room hire in Tamworth" +
    (priceValue(d.price_hourly) ? ` from £${priceValue(d.price_hourly)} per hour` : "") +
    ". Modern, professional space with a 65\" screen, free WiFi and parking — ideal for meetings, interviews, training and podcast recording.";

  return (
    <div data-testid="meeting-room-page">
      <Seo
        title="Meeting Room Hire in Tamworth"
        description={seoDescription}
        image={seoImage ? mediaUrl(seoImage) : ""}
        jsonLd={serviceSchema}
      />

      {/* Hero */}
      <section className="numbers-pattern text-white">
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <span className="text-orange font-bold text-sm tracking-widest uppercase">{d.eyebrow || "Meeting Room Hire"}</span>
          <h1 className="font-display font-extrabold text-4xl md:text-6xl uppercase leading-[0.95] mt-3">{d.heading}</h1>
          {tagline && <p className="mt-6 text-slate-300 max-w-2xl mx-auto leading-relaxed text-lg">{tagline}</p>}
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <SectionTitle eyebrow="A professional space" title="Meet, present and collaborate" center />
            <div className="space-y-4 text-center">
              {restIntro.map((p, i) => <p key={i} className="text-slate-600 leading-relaxed text-lg">{p}</p>)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Photo gallery (3 pictures) */}
      {gallery.length > 0 && (
        <section className="pb-16 md:pb-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className={`grid gap-6 ${gallery.length === 1 ? "max-w-2xl mx-auto" : gallery.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`} data-testid="mr-gallery">
              {gallery.map((img, i) => (
                <div key={i} className="rounded-2xl overflow-hidden shadow-lg border border-slate-100 bg-slate-100 aspect-[4/3]" data-testid={`mr-gallery-image-${i + 1}`}>
                  <img loading="lazy" decoding="async" src={mediaUrl(img, 1000)} alt={`CASS meeting room in Tamworth ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Prices */}
      {prices.length > 0 && (
        <section className="py-16 md:py-20 bg-navy text-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-orange font-bold text-sm tracking-widest uppercase">Simple, fair rates</span>
              <h2 className="font-display font-extrabold text-3xl md:text-4xl mt-2">Meeting Room Hire Prices</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-6" data-testid="mr-prices">
              {prices.map((p, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center hover:border-orange/50 transition-colors">
                  <p className="font-display font-extrabold text-3xl text-orange">{p.amount}</p>
                  {p.note && <p className="text-slate-300 text-sm mt-3 leading-relaxed">{p.note}</p>}
                </div>
              ))}
            </div>
            {d.discount_note && <p className="text-center text-slate-300 italic mt-8 max-w-2xl mx-auto">{d.discount_note}</p>}
          </div>
        </section>
      )}

      {/* What's Included + Room Capacity */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14">
          <Reveal>
            <SectionTitle eyebrow="Everything you need" title="What's Included" />
            <p className="text-slate-600 mb-6">Room hire includes access to a comfortable and professional space with:</p>
            <CheckList items={lines(d.included)} testid="mr-included" />
            <div className="flex flex-wrap gap-4 mt-8">
              {[{ Icon: Monitor, t: "65\" screen" }, { Icon: Wifi, t: "Free WiFi" }, { Icon: Coffee, t: "Tea & coffee" }].map(({ Icon, t }) => (
                <span key={t} className="inline-flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-full px-4 py-2 text-sm font-semibold text-navy"><Icon size={16} className="text-orange" /> {t}</span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <SectionTitle eyebrow="Room capacity" title="Suitable for small groups" />
            <CheckList items={lines(d.capacity)} testid="mr-capacity" />
            <div className="mt-8 inline-flex items-center gap-3 bg-orange/5 border border-orange/20 rounded-2xl px-5 py-4">
              <Users size={22} className="text-orange" />
              <span className="text-sm font-semibold text-navy">A quiet, professional environment to focus, collaborate and present.</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Perfect For */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <SectionTitle eyebrow="Ideal uses" title="Perfect For" center />
          <p className="text-slate-600 text-center max-w-2xl mx-auto mb-10">Our Tamworth meeting rooms are frequently used for:</p>
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-3 max-w-3xl mx-auto" data-testid="mr-perfect-for">
            {lines(d.perfect_for).map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-orange/10 text-orange flex items-center justify-center shrink-0 mt-0.5"><Check size={14} strokeWidth={3} /></span>
                <span className="text-slate-600 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Convenient Location + Breakout + Recording */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-12">
          <Reveal>
            <SectionTitle eyebrow="Easy to reach" title="Convenient Location" />
            <p className="text-slate-600 mb-6">Based in Tamworth, with easy access from the A5 and M42. Visitors benefit from:</p>
            <CheckList items={lines(d.location)} testid="mr-location" />
          </Reveal>
          <Reveal delay={80}>
            <SectionTitle eyebrow="Space to breathe" title="Breakout Area" />
            {paras(d.breakout_body).map((p, i) => <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>)}
          </Reveal>
          <Reveal delay={160}>
            <SectionTitle eyebrow="Lights, camera, action" title="Content Recording" />
            <p className="text-slate-600 mb-6">The large screen and quiet environment make it ideal for:</p>
            <CheckList items={lines(d.recording)} testid="mr-recording" />
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="pb-16 md:pb-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-100" data-testid="mr-map">
            <iframe
              title="CASS office location in Tamworth"
              src={`https://www.google.com/maps?q=${encodeURIComponent(company.address)}&output=embed`}
              width="100%"
              height="400"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <p className="text-center text-slate-500 text-sm mt-4 flex items-center justify-center gap-2">
            <MapPin size={15} className="text-orange" /> {company.address}
          </p>
        </div>
      </section>

      {/* Book CTA */}
      <section className="py-16 md:py-20 bg-navy text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mb-4">Book the Meeting Room</h2>
          {d.cta_body && <p className="text-slate-300 mb-8 leading-relaxed">{d.cta_body}</p>}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            {d.cta_email && <a href={`mailto:${d.cta_email}`} data-testid="mr-email" className="inline-flex items-center gap-2 text-slate-200 hover:text-orange transition-colors font-semibold"><Mail size={18} className="text-orange" /> {d.cta_email}</a>}
            {d.cta_phone && <a href={`tel:${d.cta_phone}`} data-testid="mr-phone" className="inline-flex items-center gap-2 text-slate-200 hover:text-orange transition-colors font-semibold"><Phone size={18} className="text-orange" /> {d.cta_phone}</a>}
          </div>
          <Link to="/contact?service=Meeting Room Hire" data-testid="mr-cta" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">
            Enquire About Hire <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
